import { useCallback, useMemo, useState, useEffect } from 'react';
import { GridReadyEvent, GridApi, ICellRendererParams } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import CommonButton from '../../../components/common-components/button/CommonButton';
import CommonDropdown, {
  Option,
} from '../../../components/common-components/dropdown/CommonDropdown';
import styles from './OvertimeManagement.module.css';
import toolbarStyles from '../../../styles/ToolbarButtons.module.css';
import { Pencil, Trash2, Plus, RotateCw, Info, FileSpreadsheet } from 'lucide-react';
import { useStore } from '../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IOvertimeResult } from '../../../models/overtime/OvertimeModel';
import { IUserManagementResult } from '../../../models/user-management/UserManagementModel';
import DeleteConfirmationDialog from './parts/DeleteConfirmationDialog';
import EditOvertimePopup from './parts/EditOvertimePopup';
import NewOvertimePopup from './parts/NewOvertimePopup';
import NewLeavePopup from './parts/NewLeavePopup';
import RejectionReasonPopup from './parts/RejectionReasonPopup';
import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';
import BaseAgGrid, {
  type GridFetchParams,
} from '../../../components/common-components/grid/BaseAgGrid';
import {
  useOvertimeManagementColumnDefs,
  useOvertimeManagementDefaultColDef,
} from './OvertimeManagement.grid';

const StatusCellRenderer = (props: ICellRendererParams<IOvertimeResult>) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Pending':
        return styles.statusPending;
      case 'Approved':
        return styles.statusApproved;
      case 'Rejected':
        return styles.statusRejected;
      default:
        return styles.statusPending;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Pending':
        return 'Beklemede';
      case 'Approved':
        return 'Onaylandı';
      case 'Rejected':
        return 'Reddedildi';
      default:
        return 'Beklemede';
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass(props.value)}`}>
      {getStatusText(props.value)}
    </span>
  );
};

const ActionCellRenderer = (props: ICellRendererParams<IOvertimeResult>) => {
  const { popupStore, overtimeStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      overtimeStore.setSelectedOvertime(props.data);
      popupStore.setIsEditOvertimeOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data?.id) {
      overtimeStore.setSelectedOvertimeToDelete(props.data.id);
      popupStore.setIsDeleteOvertimeConfirmationOpen(true);
    }
  };

  const handleShowRejectionReason = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      overtimeStore.setSelectedOvertime(props.data);
      popupStore.setIsRejectionReasonOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
      {props.data?.status === 'Rejected' && props.data?.rejectionReason && (
        <button
          onClick={handleShowRejectionReason}
          className={styles.infoButton}
          title="Red Sebebini Görüntüle"
        >
          <Info size={20} />
        </button>
      )}
    </div>
  );
};

const OvertimeManagement = () => {
  const [gridApi, setGridApi] = useState<GridApi<IOvertimeResult> | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [users, setUsers] = useState<IUserManagementResult[]>([]);
  const { popupStore, overtimeStore, authStore } = useStore();

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Admin ise kullanıcı listesini yükle
  useEffect(() => {
    const loadUsers = async () => {
      if (authStore.isAdmin) {
        try {
          const usersList = await userManagementService.getAllUsers();
          // OData yanıtı { value: [...] } formatında geliyor
          const actualUsers = Array.isArray(usersList)
            ? usersList
            : (usersList as any)?.value || [];
          setUsers(actualUsers);
        } catch (error) {
          console.error('Error loading users:', error);
        }
      }
    };

    loadUsers();
  }, [authStore.isAdmin]);

  const fetchRows = useCallback(
    async (params: GridFetchParams) => {
      const result = await overtimeStore.listOvertimes(params);
      return { rows: result.rows, lastRow: result.lastRow };
    },
    [overtimeStore]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<IOvertimeResult>) => {
      setGridApi(params.api);
      overtimeStore.setGridApi(params.api);
    },
    [overtimeStore]
  );

  const exportToExcel = useCallback(async () => {
    if (!gridApi) return;

    gridApi.showLoadingOverlay();

    try {
      const result = await overtimeStore.listOvertimes({
        startRow: 0,
        endRow: 1000000,
        filterModel: gridApi.getFilterModel(),
        sortModel: null,
      });

      if (!result?.rows.length) {
        gridApi.hideOverlay();
        return;
      }

      const exportData = result.rows.map(item => ({
        ID: item.id,
        Kullanıcı: item.userName,
        'Başlangıç Tarihi': new Date(item.startDate).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        'Bitiş Tarihi': new Date(item.endDate).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        'Toplam Saat': item.totalHours,
        Açıklama: item.description,
        Tür: item.overtimeType === 'Overtime' ? 'Mesai' : 'İzin',
        Durum:
          item.status === 'Pending'
            ? 'Beklemede'
            : item.status === 'Approved'
            ? 'Onaylandı'
            : 'Reddedildi',
        'Oluşturulma Tarihi': new Date(item.createdDate).toLocaleDateString('tr-TR'),
        Onaylayan: item.approvedBy || '',
        'Onay Tarihi': item.approvedDate
          ? new Date(item.approvedDate).toLocaleDateString('tr-TR')
          : '',
        'Red Sebebi': item.rejectionReason || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mesai Kayitlari');
      XLSX.writeFile(wb, `Mesai_Kayitlari_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      gridApi.hideOverlay();
    }
  }, [gridApi, overtimeStore]);

  const showStatusColumn = authStore.hasAnyRole(['Admin']);
  const columnDefs = useOvertimeManagementColumnDefs(
    ActionCellRenderer,
    StatusCellRenderer,
    showStatusColumn
  );
  const defaultColDef = useOvertimeManagementDefaultColDef();

  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth >= 768 && viewportWidth < 1024;
  const gridRowHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const gridHeaderHeight = isMobile ? 40 : isTablet ? 44 : 48;
  const pageSize = isMobile ? 10 : 20;

  const handleNewOvertime = () => {
    popupStore.setIsNewOvertimeOpen(true);
  };

  const handleNewLeave = () => {
    popupStore.setIsNewLeaveOpen(true);
  };

  const handleUserChange = (userId: string | null) => {
    // "all" seçildiğinde null olarak ayarla
    overtimeStore.setSelectedUserId(userId === 'all' ? null : userId);
    overtimeStore.refreshGrid();
  };

  const getRowStyle = (params: any) => {
    if (!params.data) return undefined;

    const status = params.data.status;

    if (status === 'Approved') {
      return { backgroundColor: '#d4edda' };
    }
    if (status === 'Pending') {
      return { backgroundColor: '#fff3cd' };
    }
    if (status === 'Rejected') {
      return { backgroundColor: '#f8d7da' };
    }

    return undefined;
  };

  const userOptions: Option<string>[] = useMemo(() => {
    const baseOptions: Option<string>[] = [{ value: 'all', label: 'Tüm Kullanıcılar' }];

    // users array olduğundan emin olalım
    if (Array.isArray(users) && users.length > 0) {
      const mappedUserOptions: Option<string>[] = users.map(user => ({
        value: user.id,
        label: `${user.userName}${
          user.firstName && user.lastName ? ` (${user.firstName} ${user.lastName})` : ''
        }`,
      }));
      return [...baseOptions, ...mappedUserOptions];
    }

    return baseOptions;
  }, [users]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.gridContainer}>
          <BaseAgGrid<IOvertimeResult>
            fetchRows={fetchRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            title="Mesai Yönetimi"
            toolbarLeft={({ refresh }) => (
              <>
                <CommonButton
                  type="primary"
                  onClick={handleNewOvertime}
                  leftIcon={<Plus size={18} />}
                  className={styles.toolbarBtnOvertime}
                >
                  Yeni Mesai Kaydı
                </CommonButton>
                <CommonButton
                  type="outline"
                  onClick={handleNewLeave}
                  leftIcon={<Plus size={18} />}
                  className={styles.toolbarBtnLeave}
                >
                  İzin Ekle
                </CommonButton>
                <CommonButton
                  type="tertiary"
                  onClick={exportToExcel}
                  leftIcon={<FileSpreadsheet size={18} />}
                  className={toolbarStyles.toolbarBtnExcel}
                >
                  Excel'e Aktar
                </CommonButton>
                <CommonButton
                  type="refresh"
                  onClick={refresh}
                  leftIcon={<RotateCw size={18} />}
                  className={toolbarStyles.toolbarBtnRefresh}
                >
                  Yenile
                </CommonButton>
              </>
            )}
            toolbarRight={
              authStore.isAdmin ? (
                <div className={styles.userSelector}>
                  <CommonDropdown
                    options={userOptions}
                    value={overtimeStore.selectedUserId ?? 'all'}
                    onChange={handleUserChange}
                    label="Kullanıcı Seçin"
                    className={styles.dropdown}
                  />
                </div>
              ) : null
            }
            getRowStyle={getRowStyle}
            paginationPageSize={pageSize}
            rowHeight={gridRowHeight}
            headerHeight={gridHeaderHeight}
          />
        </div>
      </div>

      {popupStore.isNewOvertimeOpen && <NewOvertimePopup />}
      {popupStore.isNewLeaveOpen && <NewLeavePopup />}
      {popupStore.isEditOvertimeOpen && <EditOvertimePopup />}
      {popupStore.isRejectionReasonOpen && <RejectionReasonPopup />}
      <DeleteConfirmationDialog
        isOpen={popupStore.isDeleteOvertimeConfirmationOpen}
        onClose={() => popupStore.setIsDeleteOvertimeConfirmationOpen(false)}
        onConfirm={async () => {
          if (overtimeStore.selectedOvertimeToDelete) {
            await overtimeStore.deleteOvertime(overtimeStore.selectedOvertimeToDelete);
            overtimeStore.setSelectedOvertimeToDelete(null);
          }
          popupStore.setIsDeleteOvertimeConfirmationOpen(false);
        }}
        title="Mesai Kaydı Silme Onayı"
        message="Bu mesai kaydını silmek istediğinizden emin misiniz?"
      />
    </>
  );
};

export default observer(OvertimeManagement);
